// 📄 components/abap/BadiCatalogGrid.tsx
// Grid filtrable de tarjetas del Catálogo de BAdIs y Objetos ABAP —
// componente compartido entre la sección de Admin (CRUD completo, ver
// BadiCatalogSection.tsx) y la vista de Herramientas del proyecto (solo
// lectura, ver BadiCatalogo.tsx): la UI de consulta/filtro/copiar es
// idéntica en ambos lugares, solo cambia si se muestran los botones de
// Editar/Eliminar.
import { useMemo, useState } from 'react';
import { Search, Copy, Check, Pencil, Trash2 } from 'lucide-react';
import {
  BadiObjeto,
  SOLUCIONES_BADI,
  TIPOS_OBJETO_ABAP,
  solucionBadiMeta,
  tipoObjetoMeta
} from '../../services/catalogoBadisService.ts';

// Item del grid: BadiObjeto (catálogo global) con los campos extra que solo
// trae un desarrollo Z (ver badiZService.ts) — todos opcionales a propósito,
// así los consumidores del catálogo global siguen pasando BadiObjeto[] tal
// cual. Mismo patrón que TransactionCatalogGrid.tsx.
type BadiCatalogItem = BadiObjeto & {
  responsable?: string;
  sistemasDisponibles?: string[];
};

interface BadiCatalogGridProps {
  badis: BadiCatalogItem[];
  // true = catálogo de BAdIs Z de un proyecto: agrega el badge "Z" y el
  // bloque de responsable/sistemas bajo la descripción de cada card.
  esZ?: boolean;
  readOnly?: boolean;
  onEdit?: (badi: BadiObjeto) => void;
  onDelete?: (badi: BadiObjeto) => void;
}

export default function BadiCatalogGrid({ badis, esZ = false, readOnly = false, onEdit, onDelete }: BadiCatalogGridProps) {
  const [busqueda, setBusqueda] = useState('');
  const [filtroSolucion, setFiltroSolucion] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [copiadoId, setCopiadoId] = useState<string | null>(null);

  const filtrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return badis.filter((b) => {
      if (filtroSolucion && b.solucion !== filtroSolucion) return false;
      if (filtroTipo && b.tipo !== filtroTipo) return false;
      if (!q) return true;
      return [b.nombre, b.implementacionZ, b.claseImplementacion, b.metodoInterfaz, b.programaInclude, b.descripcion]
        .join(' ')
        .toLowerCase()
        .includes(q);
    });
  }, [badis, busqueda, filtroSolucion, filtroTipo]);

  const handleCopiar = async (badi: BadiObjeto) => {
    const valor = badi.metodoInterfaz || badi.implementacionZ || badi.programaInclude || badi.nombre;
    try {
      await navigator.clipboard.writeText(valor);
      setCopiadoId(badi.id);
      setTimeout(() => setCopiadoId((prev) => (prev === badi.id ? null : prev)), 1500);
    } catch (err) {
      console.error('Error al copiar al portapapeles:', err);
    }
  };

  const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500";

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="relative sm:col-span-1">
          <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar por nombre, método, clase..."
            className={`${inputClass} pl-9`}
          />
        </div>
        <select className={inputClass} value={filtroSolucion} onChange={(e) => setFiltroSolucion(e.target.value)}>
          <option value="">Todas las soluciones</option>
          {SOLUCIONES_BADI.map((s) => (
            <option key={s.id} value={s.id}>{s.label}</option>
          ))}
        </select>
        <select className={inputClass} value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)}>
          <option value="">Todos los tipos</option>
          {TIPOS_OBJETO_ABAP.map((t) => (
            <option key={t.id} value={t.id}>{t.label}</option>
          ))}
        </select>
      </div>

      {filtrados.length === 0 ? (
        <p className="text-sm text-gray-500 bg-white/5 border border-white/10 rounded-2xl p-6 text-center">
          {badis.length === 0 ? 'Todavía no hay objetos registrados en el catálogo.' : 'Ningún objeto coincide con el filtro.'}
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtrados.map((badi) => {
            const tipoMeta = tipoObjetoMeta(badi.tipo);
            const solMeta = solucionBadiMeta(badi.solucion);
            return (
              <div key={badi.id} className="bg-white/5 border border-white/10 rounded-2xl p-4 flex flex-col gap-3">
                <div className="flex items-start justify-between gap-2">
                  <h4 className="text-sm font-semibold text-white leading-snug break-words">{badi.nombre}</h4>
                  <button
                    type="button"
                    onClick={() => handleCopiar(badi)}
                    title="Copiar Nombre/Método"
                    className="flex-shrink-0 p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                  >
                    {copiadoId === badi.id ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>

                <div className="flex flex-wrap items-center gap-1.5">
                  {esZ && (
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border text-amber-300 bg-amber-500/10 border-amber-500/30">
                      Z
                    </span>
                  )}
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border text-indigo-300 bg-indigo-500/10 border-indigo-500/30">
                    {solMeta.label}
                  </span>
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border text-purple-300 bg-purple-500/10 border-purple-500/30">
                    {tipoMeta.label}
                  </span>
                  {badi.transaccion && (
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border text-amber-300 bg-amber-500/10 border-amber-500/30 font-mono">
                      Tx: {badi.transaccion}
                    </span>
                  )}
                </div>

                <div className="text-xs text-gray-400 space-y-1 font-mono">
                  {badi.implementacionZ && <p className="truncate"><span className="text-gray-500">Impl. Z:</span> {badi.implementacionZ}</p>}
                  {badi.claseImplementacion && <p className="truncate"><span className="text-gray-500">Clase:</span> {badi.claseImplementacion}</p>}
                  {badi.metodoInterfaz && <p className="truncate"><span className="text-gray-500">Método/Interfaz:</span> {badi.metodoInterfaz}</p>}
                  {badi.programaInclude && <p className="truncate"><span className="text-gray-500">Prog./Include:</span> {badi.programaInclude}</p>}
                </div>

                {badi.descripcion && (
                  <p className="text-xs text-gray-400 leading-relaxed">{badi.descripcion}</p>
                )}

                {esZ && (badi.responsable || (badi.sistemasDisponibles && badi.sistemasDisponibles.length > 0)) && (
                  <div className="pt-2 border-t border-white/5 space-y-1.5">
                    {badi.responsable && (
                      <p className="text-[11px] text-gray-500">Responsable: <span className="text-gray-300">{badi.responsable}</span></p>
                    )}
                    {badi.sistemasDisponibles && badi.sistemasDisponibles.length > 0 && (
                      <div className="flex items-center gap-1">
                        {badi.sistemasDisponibles.map((s) => (
                          <span key={s} className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border text-sky-300 bg-sky-500/10 border-sky-500/30">
                            {s}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {!readOnly && (onEdit || onDelete) && (
                  <div className="flex items-center justify-end gap-1.5 pt-2 mt-auto border-t border-white/5">
                    {onEdit && (
                      <button
                        type="button"
                        onClick={() => onEdit(badi)}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                      >
                        <Pencil className="w-3.5 h-3.5" /> Editar
                      </button>
                    )}
                    {onDelete && (
                      <button
                        type="button"
                        onClick={() => onDelete(badi)}
                        className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                        title="Eliminar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
